import type { InventoryPosition } from "@/api/inventory";

/**
 * Одна номенклатура, посчитанная в НЕСКОЛЬКИХ вариантах.
 *
 * Позиция инвентаризации — это ключ (номенклатура, характеристика, партия), и
 * в документ 1С каждая позиция уезжает СВОЕЙ строкой. Поэтому одна и та же
 * номенклатура, посчитанная двумя людьми по-разному («ОПТ» у одного, «без
 * характеристики» у другого), даёт в документе две строки — склад читает их
 * как «две одинаковые строки вместо одной» (прод, 22.09.2026, НФНФ-000059:
 * «Набор: крем 30» — 1 шт как «ОПТ» и 10 шт без характеристики).
 *
 * ⚠️ Это НЕ ошибка сама по себе: у номенклатуры бывают честно разные
 * характеристики и партии, и какая верна — знает только человек. Поэтому
 * функция ничего не сливает и ничего не запрещает: она только СОБИРАЕТ факт,
 * чтобы показать его до создания документа, пока сканы ещё можно поправить.
 */
export interface SplitVariant {
  /** Как показывать характеристику; пустая — общая по порталу подпись. */
  characteristic: string;
  /** Номер партии; пусто — партии нет. */
  partia: string;
  /** Насчитано по этому варианту. */
  qty: number;
  unit: string;
}

export interface SplitNomenclature {
  nomenclature_ref: string;
  nomenclature_name: string;
  /** Всегда больше одного — иначе группы бы не было. */
  variants: SplitVariant[];
}

/** Подпись характеристики: пустая — как везде в портале. */
export const NO_CHAR_LABEL = "без характеристики";

/** Поля позиции, которые тут нужны, — чтобы функция не требовала целиком. */
type Countable = Pick<
  InventoryPosition,
  | "nomenclature_ref"
  | "nomenclature_name"
  | "characteristic_ref"
  | "characteristic_name"
  | "partia_ref"
  | "partia_name"
  | "qty_scanned"
  | "unit_name"
  | "counted"
>;

const byText = (a: string, b: string): number => a.localeCompare(b, "ru");

/**
 * Номенклатуры, посчитанные больше чем в одном варианте.
 *
 * Считаем ТОЛЬКО посчитанные позиции (`counted`): в документ уходят именно они
 * (`inventory/creation.py::_countable`), а непосчитанные — нет, и пугать ими
 * человека нечем.
 *
 * Порядок стабильный (по названию номенклатуры, внутри — по характеристике и
 * партии): текст предупреждения перечитывают, и он не должен прыгать от
 * ответа к ответу.
 */
export function findSplitPositions(positions: Countable[]): SplitNomenclature[] {
  const groups = new Map<string, { name: string; variants: SplitVariant[] }>();

  for (const p of positions) {
    if (!p.counted) continue;
    const g = groups.get(p.nomenclature_ref);
    const variant: SplitVariant = {
      characteristic: p.characteristic_name || NO_CHAR_LABEL,
      partia: p.partia_name,
      qty: p.qty_scanned,
      unit: p.unit_name,
    };
    if (g) g.variants.push(variant);
    else groups.set(p.nomenclature_ref, { name: p.nomenclature_name, variants: [variant] });
  }

  return [...groups.entries()]
    .filter(([, g]) => g.variants.length > 1)
    .map(([ref, g]) => ({
      nomenclature_ref: ref,
      nomenclature_name: g.name,
      variants: [...g.variants].sort(
        (a, b) => byText(a.characteristic, b.characteristic) || byText(a.partia, b.partia),
      ),
    }))
    .sort(
      (a, b) =>
        byText(a.nomenclature_name, b.nomenclature_name) ||
        byText(a.nomenclature_ref, b.nomenclature_ref),
    );
}

/** Вариант одной строкой: «ОПТ · партия П-7 — 3 шт». */
export function splitVariantText(v: SplitVariant): string {
  const head = v.partia ? `${v.characteristic} · партия ${v.partia}` : v.characteristic;
  return `${head} — ${v.qty} ${v.unit}`;
}
